import React from 'react';
import { View, Text, TouchableOpacity, Modal, TouchableWithoutFeedback, ScrollView, FlatList, Platform } from 'react-native';
import { SvgXml } from 'react-native-svg';
import { useTheme } from '../../../../../theme';
import type { CalendarEvent, CalendarStyleConfig } from '../types';
import { emailToDisplayName } from '../../../../../screens/ActionPlan/utils/formOptions';

const EyeIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.99984 4.33333C10.5265 4.33333 12.7798 5.75333 13.8798 8C12.7798 10.2467 10.5332 11.6667 7.99984 11.6667C5.4665 11.6667 3.21984 10.2467 2.11984 8C3.21984 5.75333 5.47317 4.33333 7.99984 4.33333ZM7.99984 3C4.6665 3 1.81984 5.07333 0.666504 8C1.81984 10.9267 4.6665 13 7.99984 13C11.3332 13 14.1798 10.9267 15.3332 8C14.1798 5.07333 11.3332 3 7.99984 3ZM7.99984 6.33333C8.91984 6.33333 9.6665 7.08 9.6665 8C9.6665 8.92 8.91984 9.66667 7.99984 9.66667C7.07984 9.66667 6.33317 8.92 6.33317 8C6.33317 7.08 7.07984 6.33333 7.99984 6.33333ZM7.99984 5C6.3465 5 4.99984 6.34667 4.99984 8C4.99984 9.65333 6.3465 11 7.99984 11C9.65317 11 10.9998 9.65333 10.9998 8C10.9998 6.34667 9.65317 5 7.99984 5Z" fill="#005CBC"/>
</svg>`;

interface EventSummaryModalProps {
  visible: boolean;
  events: CalendarEvent[];
  date: Date | null;
  onClose: () => void;
  onEventPress?: (event: CalendarEvent) => void;
  onViewDetails?: (event: CalendarEvent) => void;
  styleConfig?: CalendarStyleConfig;
}

/**
 * EventSummaryModal Component
 * Displays a list of all events for a selected day
 */
export const EventSummaryModal: React.FC<EventSummaryModalProps> = ({
  visible,
  events,
  date,
  onClose,
  onEventPress,
  onViewDetails,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  if (!date || events.length === 0) {
    return null;
  }

  // Format day name without timezone conversion
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[date.getDay()];
  
  // Format event date WITHOUT timezone conversion
  const formatEventDate = (eventDate: Date): string => {
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[eventDate.getMonth()];
      const day = eventDate.getDate();
      const year = eventDate.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch (e) {
      return '--';
    }
  };

  const getStatusColor = (event: CalendarEvent) => {
    const assignmentStatus = event.actionPlanData?.ASSIGNMENT_STATUS?.toLowerCase();
    const status = event.actionPlanData?.STATUS?.toLowerCase();
    
    // Check ASSIGNMENT_STATUS first
    if (assignmentStatus === 'declined') {
      return '#D40020'; // Red for declined
    }
    
    // Then check STATUS
    if (status === 'completed' || event.isDone) {
      return '#008553'; // Green for completed
    }
    if (status === 'in_progress') {
      return '#316BB8'; // Blue for in progress
    }
    if (status === 'overdue') {
      return '#CC6E16'; // Orange for overdue
    }
    return '#316bb8'; // Default blue
  };

  const getStatusLabel = (event: CalendarEvent) => {
    const assignmentStatus = event.actionPlanData?.ASSIGNMENT_STATUS?.toLowerCase();
    
    if (assignmentStatus === 'declined') {
      return 'Declined';
    }
    
    const status = event.actionPlanData?.STATUS || 'pending';
    return status === 'in_progress' 
      ? 'In Progress' 
      : status === 'completed' 
      ? 'Completed' 
      : status === 'overdue'
      ? 'Overdue'
      : status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  const renderEventItem = ({ item: event, index }: { item: CalendarEvent; index: number }) => (
    <View key={event.id}>
      <View style={{ marginBottom: 16 }}>
        {/* Event Title and Status */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b', marginBottom: 4 }}>
              Title
            </Text>
            <Text style={{ fontSize: 12, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d', lineHeight: 18, textTransform: 'capitalize' }} numberOfLines={2}>
              {event.title}
            </Text>
          </View>
          <View
            style={{
              backgroundColor: getStatusColor(event),
              borderRadius: 4,
              paddingHorizontal: 8,
              paddingVertical: 4,
            }}
          >
            <Text style={{ fontSize: 10, fontWeight: '600', color: '#ffffff', lineHeight: 14 }}>
              {getStatusLabel(event)}
            </Text>
          </View>
        </View>

        {/* Assigned User and Target Date */}
        <View style={{ flexDirection: 'row', gap: 16, marginBottom: 12 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b', marginBottom: 4 }}>
              Assigned User
            </Text>
            <Text style={{ fontSize: 12, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d' }}>
              {event.actionPlanData?.ASSIGNEE_NAME || emailToDisplayName(event.actionPlanData?.ASSIGNEE_EMAIL || '') || event.actionPlanData?.ASSIGNEE_GPID || (event.color ? 'Event' : 'Unassigned')}
            </Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 10, fontWeight: '400', color: isDark ? colors.text.secondary : '#4b4b4b', marginBottom: 4 }}>
              Target date
            </Text>
            <Text style={{ fontSize: 12, fontWeight: '500', color: isDark ? colors.text.primary : '#2d2d2d' }}>
              {formatEventDate(event.date)}
            </Text>
          </View>
        </View>

        {/* View Details Button */}
        <TouchableOpacity
          style={{
            borderWidth: 1,
            borderColor: isDark ? colors.brand.primary : '#005cbc',
            backgroundColor: isDark ? colors.background.elevated : '#fbfcfe',
            borderRadius: 24,
            height: 24,
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: 8,
            paddingVertical: 4,
            alignSelf: 'flex-start',
            flexDirection: 'row',
            gap: 4,
          }}
          onPress={() => {
            onClose();
            if (onViewDetails) {
              setTimeout(() => {
                onViewDetails(event);
              }, 350);
            } else {
              setTimeout(() => {
                onEventPress?.(event);
              }, 350);
            }
          }}
        >
          <SvgXml xml={EyeIcon} width="14" height="14" />
          <Text style={{ fontSize: 12, fontWeight: '700', color: isDark ? colors.brand.primary : '#005cbc' }}>View details</Text>
        </TouchableOpacity>
      </View>
      {index < events.length - 1 && <View style={{ height: 1, backgroundColor: isDark ? colors.border.default : '#ebebeb', marginBottom: 12 }} />}
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <TouchableWithoutFeedback>
            <View
              style={{
                backgroundColor: isDark ? colors.background.surface : '#ffffff',
                borderRadius: 8,
                padding: 16,
                width: '85%',
                maxWidth: 400,
                maxHeight: '80%',
              }}
            >
              {/* Header */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: isDark ? colors.text.primary : '#2d2d2d', flex: 1 }}>
                  {dayName}
                </Text>
                <TouchableOpacity onPress={onClose} style={{ padding: 4 }}>
                  <Text style={{ fontSize: 24, fontWeight: 'bold', color: isDark ? colors.text.primary : '#3a3a3a' }}>×</Text>
                </TouchableOpacity>
              </View>

              {/* Events List */}
              <ScrollView
                style={{
                  borderWidth: 1,
                  borderColor: isDark ? colors.border.default : '#ebebeb',
                  backgroundColor: isDark ? colors.background.elevated : undefined,
                  borderRadius: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 12,
                }}
                showsVerticalScrollIndicator={true}
              >
                <FlatList
                  data={events}
                  renderItem={renderEventItem}
                  keyExtractor={(item) => item.id}
                  scrollEnabled={false}
                />
              </ScrollView>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default EventSummaryModal;
